import { useI18n } from 'vue-i18n';
import type { PassengerType } from '@aviata/platform-client';
import type {
  Passengers,
  CabinClass,
} from '../../../entities/search-form-state';

import ru from '../config/locales/ru.json';
import kk from '../config/locales/kk.json';
import en from '../config/locales/en.json';

const paxTypeLocale = {
  adt: 'adultShort',
  chd: 'childShort',
  inf: 'infantShort',
} as const;

const nonYouthPassenger = (
  key: PassengerType,
): key is Exclude<PassengerType, 'yth'> => {
  return key !== 'yth';
};

const getPassengersWithoutYouth = (passengers: Passengers) => {
  return (Object.keys(passengers) as Array<PassengerType>)
    .filter(nonYouthPassenger)
    .map(key => ({ localeKey: paxTypeLocale[key], value: passengers[key] }));
};

export function usePassengersText() {
  const { t } = useI18n({
    messages: { ru, kk, en },
  });

  const getPassengersAmountText = (
    passengers: Passengers,
    isYouth?: boolean,
  ) => {
    if (isYouth) {
      const { yth } = passengers;
      return t('passengersSelect.passengers.amount', yth);
    }

    const withoutYouth = getPassengersWithoutYouth(passengers);
    const nonZeroPassengers = withoutYouth.filter(({ value }) => value > 0);
    const isShort = nonZeroPassengers.length > 1;
    const { adt } = passengers;

    return isShort
      ? nonZeroPassengers
          .map(({ localeKey, value }) =>
            t(`passengersSelect.passengers.${localeKey}`, value),
          )
          .join(', ')
      : t('passengersSelect.passengers.amount', adt);
  };

  const getCabinClassText = (cabinClass: CabinClass, isYouth?: boolean) => {
    if (cabinClass === 'E' && !isYouth) {
      return t('passengersSelect.cabinClass.economy');
    }
    if (cabinClass === 'B') {
      return t('passengersSelect.cabinClass.business');
    }
    return t('passengersSelect.cabinClass.youth');
  };

  return {
    getCabinClassText,
    getPassengersAmountText,
  };
}
